import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, ArrowRight } from 'lucide-react';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      toast.success('Password reset successfully!');
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--neon-bg)' }}>
        <div className="glass-card rounded-2xl p-8 text-center">
          <p style={{ color: 'var(--neon-secondary)' }}>Invalid reset link. No token provided.</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--neon-bg)' }}>
        <div className="glass-card rounded-2xl p-8 text-center">
          <p className="text-lg font-bold mb-4" style={{ color: 'var(--neon-primary)' }}>Password Reset Successful!</p>
          <a href="/login" className="btn-neon-primary inline-flex items-center gap-2">
            Go to Login <ArrowRight size={16} />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--neon-bg)' }}>
      <div className="glass-card rounded-2xl p-8 w-full max-w-md fade-in-up">
        <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-primary)' }}>
          Reset Password
        </h2>
        <p className="text-sm mb-6" style={{ color: 'var(--neon-text-secondary)' }}>
          Enter your new password below.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest mb-2 block" style={{ color: 'var(--neon-text-muted)' }}>
              <Lock size={12} className="inline mr-1" /> New Password
            </Label>
            <Input data-testid="reset-password-input" className="neon-input" type="password" required
              placeholder="Min 6 characters" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest mb-2 block" style={{ color: 'var(--neon-text-muted)' }}>
              <Lock size={12} className="inline mr-1" /> Confirm Password
            </Label>
            <Input data-testid="reset-confirm-input" className="neon-input" type="password" required
              placeholder="Repeat password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
          <button type="submit" data-testid="reset-submit-btn" disabled={loading}
            className="w-full btn-neon-primary disabled:opacity-50">
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
